import { dateAdd, DateRange, dateSub, Holiday, RosterNoteDTO, SchoolHoliday, VacationPolicy } from "@pentacode/core";
import { debounce } from "../lib/util";
import { RosterNotePopover } from "./roster-note-popover";
import { css, html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { app } from "../init";
import { singleton } from "../lib/singleton";
import { shared } from "../styles";
import { DateString } from "@pentacode/openapi";

type Entry = {
    start: DateString;
    end: DateString;
    text: string;
    color: string;
    icon: string;
    rosterNote?: RosterNoteDTO;
    borderStyle?: string;
};

@customElement("ptc-roster-notes")
export class RosterNotes extends LitElement {
    @property({ attribute: false })
    rosterNotes: RosterNoteDTO[] = [];

    @property({ attribute: false })
    schoolHolidays: SchoolHoliday[] = [];

    @property({ attribute: false })
    holidays: Holiday[] = [];

    @property({ attribute: false })
    dateRange: DateRange;

    private _resizeObserver?: ResizeObserver;

    @singleton("ptc-roster-note-popover")
    private _rosterNotePopover: RosterNotePopover;

    private get _entries(): Entry[] {
        return [
            ...this.rosterNotes.map((note) => ({
                start: note.start,
                end: note.end,
                text: note.text,
                color: note.color || "",
                icon: note.vacationPolicy !== VacationPolicy.AllowAll ? "ban" : "",
                rosterNote: note,
            })),
            ...this.schoolHolidays.map((holiday) => ({
                start: holiday.start,
                end: holiday.end,
                text: holiday.name,
                color: "var(--grey)",
                icon: "school-circle-xmark",
                borderStyle: "dashed",
            })),
            ...this.holidays.map((holiday) => ({
                start: holiday.date,
                end: dateAdd(holiday.date, { days: 1 }),
                text: holiday.name,
                color: "var(--purple)",
                icon: "wreath",
                borderStyle: "dotted",
            })),
        ];
    }

    private get _lanes() {
        const lanes: Entry[][] = [];

        for (const note of this._entries) {
            let lane = lanes.find((notes) => !notes.some((n) => n.start < note.end && n.end > note.start));
            if (!lane) {
                lane = [];
                lanes.push(lane);
            }
            lane.push(note);
        }
        return lanes;
    }

    connectedCallback() {
        super.connectedCallback();

        if (!this._resizeObserver) {
            this._resizeObserver = new ResizeObserver(debounce(() => this.requestUpdate(), 200));
            this._resizeObserver.observe(this);
        }
    }

    async addRosterNote(note: RosterNoteDTO) {
        this.rosterNotes = [...this.rosterNotes, note];
        await this.updateComplete;
        setTimeout(() => this._editRosterNote(note), 100);
    }

    private _editRosterNote(note: RosterNoteDTO) {
        this._rosterNotePopover.hide();
        if (!app.hasPermission(`manage.roster.notes`)) {
            return;
        }
        const noteEl = this.renderRoot.querySelector(`[data-roster-note="${note.id || "new"}"]`) as HTMLElement;
        const change = () => this.requestUpdate();
        const done = (e: CustomEvent<{ deleted?: boolean; edited?: boolean }>) => {
            this._rosterNotePopover.removeEventListener("change", change);
            this._rosterNotePopover.removeEventListener("done", done);
            if (e.detail?.deleted) {
                const index = this.rosterNotes.indexOf(note);
                this.rosterNotes.splice(index, 1);
                void this.requestUpdate();
            }
            if (e.detail?.edited || e.detail?.deleted) {
                this.dispatchEvent(new CustomEvent("roster-notes-updated"));
            }
        };
        this._rosterNotePopover.addEventListener("change", change);
        this._rosterNotePopover.addEventListener("done", done);
        this._rosterNotePopover.rosterNote = note;
        this._rosterNotePopover.requestUpdate("rosterNote");
        void this._rosterNotePopover.showAt(noteEl, true);
    }

    private _getRosterNoteStyle(entry: Entry) {
        const dayWidth = this.offsetWidth / dateSub(this.dateRange.from, this.dateRange.to);
        const startDate = entry.start < this.dateRange.from ? this.dateRange.from : entry.start;
        const endDate = entry.end > this.dateRange.to ? this.dateRange.to : entry.end;
        const startIndex = dateSub(this.dateRange.from, startDate);
        const noteLength = dateSub(startDate, endDate);
        const left = startIndex * dayWidth;
        const width = noteLength * dayWidth;

        let styles = `left: ${left}px; width: ${width}px; --color-highlight: ${entry.color}; --border-style: ${entry.borderStyle || "solid"};`;
        if (entry.start < this.dateRange.from) {
            styles += "--border-radius: 0 0.5em 0.5em 0;";
        }
        if (entry.end > this.dateRange.to) {
            styles += "--border-radius: 0.5em 0 0 0.5em;";
        }
        return styles;
    }

    static styles = [
        shared,
        css`
            .roster-note-lane {
                height: 2em;
                position: relative;
            }

            .roster-note {
                position: absolute;
                left: 0;
                right: 0;
                top: 0;
                bottom: 0;
            }

            .roster-note-inner {
                color: var(--color-highlight, var(--color-primary));
                position: absolute;
                left: 0.1em;
                right: 0.1em;
                bottom: 0.1em;
                top: 0.1em;
                border-radius: var(--border-radius, 0.5em);
                border: solid 1px;
                text-align: center;
                padding: 0 0.3em;
                font-size: var(--font-size-small);
                line-height: 1.9em;
                border-style: var(--border-style, solid);
            }
        `,
    ];

    render() {
        return html`
            <div class="roster-notes">
                ${this._lanes.map(
                    (entries) =>
                        html` <div class="roster-note-lane">
                            ${entries.map(
                                (entry) => html`
                                    <div
                                        class="roster-note"
                                        style="${this._getRosterNoteStyle(entry)}"
                                        data-roster-note="${entry.rosterNote ? entry.rosterNote.id || "new" : ""}"
                                    >
                                        <div
                                            class="roster-note-inner ellipsis ${entry.rosterNote ? "click" : ""}"
                                            @click=${entry.rosterNote
                                                ? () => this._editRosterNote(entry.rosterNote!)
                                                : nothing}
                                            title=${entry.text}
                                        >
                                            ${entry.icon
                                                ? html`<i class="${entry.icon}" style="margin-left: -0.1em;"></i>`
                                                : ""}
                                            ${entry.text.split("\n")[0]}
                                        </div>
                                    </div>
                                `
                            )}
                        </div>`
                )}
            </div>
        `;
    }
}
